/**
 * Red banner while a payout alert is open (spec 0010 §7, item 22).
 *
 * Reads ``GET /admin-api/v1/payout-health/`` (scoped to the operator's
 * tenants) every 60 s. ``page``: an unacknowledged PayoutAlert, or a
 * payout unresolved for 30 min. The second stays red after its alert is
 * acknowledged, until the payout leaves ``processing``. ``warn``: parked
 * or unresolved payouts, or callbacks dead-lettered in the last 7 days.
 * Nothing renders when all is well. If the first read fails, a muted
 * "unknown" line says so instead of looking like "all clear"; a later
 * failed refresh keeps the last result. Never blocks the shell.
 *
 * Read-only: acknowledging is SUPER + step-up through
 * ``POST /admin-api/v1/payout-alerts/{id}/acknowledge/`` (operator
 * runbook); there is no acknowledge button yet.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getPayoutHealth } from "@/lib/api";

const REFRESH_MS = 60_000;
const STUCK_PAGE_S = 30 * 60;

export function PayoutHealthBanner() {
  const query = useQuery({
    queryKey: ["payout-health"] as const,
    queryFn: getPayoutHealth,
    refetchInterval: REFRESH_MS,
  });
  const health = query.data;
  if (!health && query.isError) {
    return (
      <Alert className="mb-6 text-muted-foreground" data-testid="payout-health-unknown">
        <AlertDescription>
          Payout health unknown: couldn’t load it. Retrying every minute.
        </AlertDescription>
      </Alert>
    );
  }
  if (!health || !health.banner) return null;
  const stuck =
    health.oldest_unresolved_age_s !== null && health.oldest_unresolved_age_s >= STUCK_PAGE_S;

  if (health.banner === "page") {
    return (
      <Alert variant="destructive" className="mb-6" data-testid="payout-health-banner">
        <AlertTitle>
          {health.open_alerts > 0
            ? `${health.open_alerts} open payout alert${health.open_alerts === 1 ? "" : "s"}`
            : "A payout has been unresolved for 30 minutes"}
        </AlertTitle>
        <AlertDescription>
          <ul className="mt-1 space-y-0.5">
            {health.latest_alerts.map((a) => (
              <li key={a.id}>
                <span className="font-mono">{a.kind}</span>
                {a.count > 1 ? ` ×${a.count}` : ""}
                {a.intent_id ? (
                  <>
                    {" "}
                    on{" "}
                    <Link className="font-mono underline" to={`/payment-intents/${a.intent_id}`}>
                      {a.intent_id}
                    </Link>
                  </>
                ) : (
                  <span className="font-mono"> ({a.tenant_id})</span>
                )}
              </li>
            ))}
          </ul>
          {stuck ? (
            <p className="mt-1">
              A payout has been processing for 30 minutes or more. This banner stays red until it
              settles, even after its alert is acknowledged.
            </p>
          ) : null}
          <p className="mt-1">
            See “Payout alerts” in the operator docs. A super admin acknowledges each alert once
            someone has looked at it.
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="mb-6" data-testid="payout-health-banner">
      <AlertTitle>Payouts need a look</AlertTitle>
      <AlertDescription>
        {health.unresolved_count} unresolved, {health.parked_count} parked,{" "}
        {health.needs_resolution_dead_letters} dead-lettered callback
        {health.needs_resolution_dead_letters === 1 ? "" : "s"}.
      </AlertDescription>
    </Alert>
  );
}

export default PayoutHealthBanner;
